import { Grid, Typography } from "@material-ui/core";
import CustomDateField from "components/CustomDateField";
import SanitizedGrid from "components/SanitizedGrid";
import StatusField from "components/StatusField";
import {
  Datagrid,
  Filter,
  TextField,
  SearchInput,
  FunctionField,
  ReferenceField,
  ListProps,
  SelectInput,
  TopToolbar,
  useDataProvider,
} from "react-admin";
import { GridinRow, GridLabel } from "utils/style";
import { SchoolIdSelected } from "utils/tools";
import {
  isMaxLevelList,
  isNotHaveRoutesList,
  scheduleTypeList,
  statusList,
} from "./const";
import React, { useEffect, useState } from "react";
import InternalNoteStudentButton from "./components/InternalNoteStudentButton";
import BusDataListField from "./components/BusDataListField";
import ScheduleType from "./components/ScheduleTypeColumn";
import ExportStudentButton from "./components/ExportStudentButton";
import ListShowButton from "../../components/ListShowButton";
import ListEditButton from "../../components/ListEditButton";
import ListWrapper from "../../components/ListWrapper";
import ActionsMenu from "../../components/ActionsMenu";
import CreateStudentSubsidyByCsvButton from "./components/CreateStudentSubsidyByCsvButton";
import { availableOnlySuperAdmin } from "utils/validation";

const StudentFilter = (props: any) => {
  const noScheduleOption = { id: "noSchedule", name: "Without Schedule" };

  return (
    <Filter {...props}>
      <SearchInput source="search" alwaysOn />
      <SelectInput
        label="Status"
        source="Status"
        choices={statusList}
        alwaysOn
      />
      <SelectInput
        label="Is Have Routes"
        source="IsNotHaveRoutes"
        choices={isNotHaveRoutesList}
        alwaysOn
      />
      <SelectInput
        label="Is Max Level"
        source="IsMaxLevel"
        choices={isMaxLevelList}
        alwaysOn
      />
      <SelectInput
        label="Schedule Type"
        source="ScheduleType"
        choices={[...scheduleTypeList, noScheduleOption]}
        alwaysOn
      />
    </Filter>
  );
};

const ActionButtons = ({ isStudentSubsidyEnable }: any) => {
  return (
    <TopToolbar>
      {isStudentSubsidyEnable && availableOnlySuperAdmin() && (
        <CreateStudentSubsidyByCsvButton />
      )}
      {availableOnlySuperAdmin() && (
        <ExportStudentButton />
      )}
    </TopToolbar>
  );
};

const StudentList = (props: ListProps) => {
  const breadcrumb = [{ link: "/students", title: "Students" }];

  const id = Number(SchoolIdSelected());
  const provider = useDataProvider();

  const [isStudentSubsidyEnable, setIsStudentSubsidyEnable] = useState(false);

  useEffect(() => {
    if (id) {
      provider
        .getOne("schools", { id })
        .then(({ data }) => {
          if (data) {
            setIsStudentSubsidyEnable(data.isStudentSubsidyEnable);
          } else {
            setIsStudentSubsidyEnable(false);
          }
        });
    } else {
      setIsStudentSubsidyEnable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <ListWrapper
        breadcrumb={breadcrumb}
        headerTitle="Students Management"
        exporter={false}
        filters={<StudentFilter />}
        filter={{
          SchoolId: id,
        }}
        sort={{ field: "CreatedAt", order: "DESC" }}
        bulkActionButtons={false}
        actions={<ActionButtons isStudentSubsidyEnable={isStudentSubsidyEnable} />}
        {...props}
        hasCreate={false}
      >
        <Datagrid>
          <FunctionField
            label="Student Info"
            render={() => (
              <SanitizedGrid container spacing={2}>
                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Id :
                  </Typography>

                  <TextField source="id" emptyText="-" />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Name :
                  </Typography>

                  <FunctionField
                    render={(record: any) =>
                      `${record.firstName} ${record.lastName}`
                    }
                    emptyText="-"
                  />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Gender :
                  </Typography>

                  <TextField source="gender" emptyText="-" />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    School :
                  </Typography>
                  <ReferenceField
                    source="schoolId"
                    reference="schools"
                    link={false}
                    emptyText="-"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Level :
                  </Typography>
                  <ReferenceField
                    source="levelId"
                    reference="levels"
                    link={false}
                    emptyText="-"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Class :
                  </Typography>
                  <ReferenceField
                    source="levelGroupId"
                    reference="levelGroups"
                    link={false}
                    emptyText="-"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </Grid>
              </SanitizedGrid>
            )}
          />
          <FunctionField
            label="Parent Info"
            render={() => (
              <SanitizedGrid container spacing={2}>
                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Id :
                  </Typography>
                  <TextField source="parent.id" />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Name :
                  </Typography>
                  <FunctionField
                    render={(record: any) =>
                      `${record.parent?.firstName} ${record.parent?.lastName}`
                    }
                  />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Email :
                  </Typography>
                  <TextField source="parent.email" />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Mobile :
                  </Typography>
                  <TextField source="parent.mobile" />
                </Grid>
              </SanitizedGrid>
            )}
          />
          <FunctionField
            label="Schedule"
            render={(record: any) => (
              <ScheduleType
                regularStatus={record.regularStatus}
                ccaStatus={record.ccaStatus}
              />
            )}
          />
          <CustomDateField
            source="createdAt"
            label="Created On"
            emptyText="-"
            isDateTime
          />
          <FunctionField
            label="Bus Data"
            render={(props: any) => <BusDataListField studentId={props.id} />}
          />
          <StatusField source="status" />
          <FunctionField
            label="Actions"
            render={(record: any) => (
              <ActionsMenu
                render={[
                  <ListShowButton record={record} />,
                  <ListEditButton record={record} />,
                  <InternalNoteStudentButton studentId={record.id} />,
                ]}
              />
            )}
          />
        </Datagrid>
      </ListWrapper>
    </>
  );
};

export default StudentList;
